import { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/NavBar";
import { useAppDispatch, useAppSelector } from "../store/store";
import AuthAction from "../store/auth/action";
import type { UserAddress } from "../models/auth/auth.model";

const MAX_ADDRESSES = 5;

const ProfileAddressesPage = () => {
    const dispatch = useAppDispatch();
    const { user, isLoading } = useAppSelector((s) => s.auth);
    const [showModal, setShowModal] = useState(false);
    const [editingAddress, setEditingAddress] = useState<UserAddress | null>(null);
    const [showLimitModal, setShowLimitModal] = useState(false);
    const [city, setCity] = useState("");
    const [street, setStreet] = useState("");

    const addresses = user?.addresses ?? [];

    const openAdd = () => {
        if (addresses.length >= MAX_ADDRESSES) {
            setShowLimitModal(true);
            return;
        }
        setEditingAddress(null);
        setCity("");
        setStreet("");
        setShowModal(true);
    };

    const openEdit = (address: UserAddress) => {
        setEditingAddress(address);
        setCity(address.city);
        setStreet(address.street);
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setEditingAddress(null);
        setCity("");
        setStreet("");
    };

    const handleSaveAddress = (e: React.FormEvent) => {
        e.preventDefault();
        if (!user || !city.trim() || !street.trim()) return;
        const newAddress: UserAddress = editingAddress
            ? { ...editingAddress, city: city.trim(), street: street.trim() }
            : { id: crypto.randomUUID?.() ?? `addr_${Date.now()}`, city: city.trim(), street: street.trim() };
        const next = editingAddress
            ? addresses.map((a) => (a.id === editingAddress.id ? newAddress : a))
            : [...addresses, newAddress];
        AuthAction.updateUserAddress(user, next, dispatch);
        closeModal();
    };

    return (
        <div className="min-h-screen bg-[#f8f2ed] font-manrope">
            <Navbar />
            <div className="max-w-xl mx-auto px-4 py-6">
                <Link to="/profile" className="text-[#704e37] text-sm font-medium mb-4 inline-block">← Prapa në profilin</Link>
                <div className="flex justify-between items-center mb-4">
                    <h1 className="text-2xl font-semibold text-[#704e37]">Adresat</h1>
                    <button
                        type="button"
                        onClick={openAdd}
                        disabled={addresses.length >= MAX_ADDRESSES}
                        className="px-4 py-2 rounded-lg bg-[#704e37] text-white font-medium hover:opacity-90 disabled:opacity-50"
                    >
                        + Shto adresë
                    </button>
                </div>

                {addresses.length === 0 ? (
                    <div className="bg-white rounded-xl shadow p-8 text-center text-gray-500">
                        <p className="font-medium text-gray-700">Ju nuk keni adresa</p>
                        <p className="text-sm mt-1">Për të shtuar një adresë të re, shtypni butonin "Shto adresë".</p>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {addresses.map((addr) => (
                            <button
                                key={addr.id}
                                type="button"
                                onClick={() => openEdit(addr)}
                                className="w-full bg-white rounded-xl shadow p-4 text-left hover:bg-gray-50 transition"
                            >
                                <p className="font-medium text-gray-800">{addr.city}</p>
                                <p className="text-sm text-gray-600">{addr.street}</p>
                            </button>
                        ))}
                    </div>
                )}
            </div>

            {showModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={closeModal}>
                    <div className="bg-white rounded-xl shadow-lg max-w-sm w-full p-6" onClick={(e) => e.stopPropagation()}>
                        <h2 className="text-lg font-semibold text-[#704e37] mb-4">
                            {editingAddress ? "Ndrysho adresën" : "Shto adresë të re"}
                        </h2>
                        <form onSubmit={handleSaveAddress} className="space-y-4">
                            <div>
                                <label className="block text-sm text-gray-600 mb-1">Qyteti</label>
                                <input
                                    value={city}
                                    onChange={(e) => setCity(e.target.value)}
                                    className="w-full border rounded px-3 py-2"
                                    placeholder="Qyteti"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm text-gray-600 mb-1">Rruga</label>
                                <input
                                    value={street}
                                    onChange={(e) => setStreet(e.target.value)}
                                    className="w-full border rounded px-3 py-2"
                                    placeholder="Rruga"
                                    required
                                />
                            </div>
                            <div className="flex gap-2">
                                <button type="button" onClick={closeModal} className="flex-1 py-2.5 rounded-lg border border-gray-300 font-medium">
                                    Anulo
                                </button>
                                <button type="submit" disabled={isLoading} className="flex-1 py-2.5 rounded-lg bg-[#704e37] text-white font-medium hover:opacity-90 disabled:opacity-50">
                                    {isLoading ? "Duke ruajtur..." : "Ruaj"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {showLimitModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setShowLimitModal(false)}>
                    <div className="bg-white rounded-xl shadow-lg max-w-sm w-full p-6" onClick={(e) => e.stopPropagation()}>
                        <p className="font-medium text-gray-800 mb-1">Njoftim</p>
                        <p className="text-gray-600 text-sm mb-4">Ju keni arritur limitin e adresave që është 5.</p>
                        <button onClick={() => setShowLimitModal(false)} className="w-full py-2.5 rounded-lg bg-[#704e37] text-white font-medium">
                            Mbyll
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProfileAddressesPage;
